// Client side of the compromised-device flow.
//
// When api() sees `X-Compromise-Signal: true` on any response (it rides a 401
// from the server's compromise gate), this orchestrates the local wipe:
//   1. tell the controlling service worker to destroy the encrypted offline
//      store (PII drafts + the device secret that derives their key) + caches,
//   2. also wipe from the app context as a fallback (no SW controller yet),
//   3. drop the in-memory access token,
//   4. hard-navigate to /login — a full reload guarantees a clean slate.
//
// Guarded so concurrent signalled responses trigger exactly one wipe.

import { setAccessToken } from './auth';
import { wipeOfflineData } from './offline';

let wiping = false;

export function handleCompromiseSignal(): void {
  if (wiping) return;
  wiping = true;
  void runWipe();
}

// resetCompromiseGuard is for tests only — the guard is a module singleton.
export function resetCompromiseGuard(): void {
  wiping = false;
}

async function runWipe(): Promise<void> {
  messageServiceWorker();
  try {
    await wipeOfflineData();
  } catch {
    // best-effort; the service worker wipe is the canonical path
  }
  setAccessToken(null);
  redirectToLogin();
}

function messageServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.controller?.postMessage({ type: 'compromised' });
}

function redirectToLogin(): void {
  // Hard navigation (matching auth.ts startOAuth), not an SPA route change: a
  // full document reload guarantees no in-memory drafts, stores, or timers
  // survive the compromise.
  if (typeof window !== 'undefined' && window.location) {
    window.location.assign('/login');
  }
}
